// Visualization-specific types
// These types are used for rendering and interaction, separate from data model

// ============================================================================
// Visualization Node Types
// ============================================================================

export type NodeType = 'site' | 'pod' | 'network' | 'rack' | 'device' | 'component';

export interface VisualizationNode {
  id: string;
  type: NodeType;
  name: string;
  x: number;
  y: number;
  z?: number;
  parent?: string;
  children?: string[];
  metadata?: NodeMetadata;
  // D3 force simulation properties (added at runtime)
  fx?: number | null;
  fy?: number | null;
  vx?: number;
  vy?: number;
}

export interface NodeMetadata {
  // Original data references
  deviceType?: string;
  componentType?: string;
  componentChoice?: string;
  instanceBaseName?: string;
  instanceIndex?: number;
  componentBaseName?: string;
  componentIndex?: number;

  // Location information (from annotations)
  location?: {
    site?: string;
    pod?: string;
    network?: string;
    rack?: string;
    position?: number;
  };

  // Component counts for aggregation display
  externalComponentCount?: number;
  totalComponentCount?: number;

  // Device role information (for autolayout)
  role?: 'spine' | 'leaf' | 'server' | 'unknown';
  roleConfidence?: number;

  // Any additional annotations
  annotations?: Record<string, any>;
}

// ============================================================================
// Visualization Link Types
// ============================================================================

export interface VisualizationLink {
  id: string;
  source: string | VisualizationNode;
  target: string | VisualizationNode;
  bandwidth?: BandwidthInfo;
  latency?: LatencyInfo;
  protocol?: string;
  metadata?: LinkMetadata;
}

export interface BandwidthInfo {
  value: number;
  unit: string; // 'Gbps', 'GB/s', 'GT/s'
}

export interface LatencyInfo {
  value: number;
  unit: string; // 'ms', 'μs', 'ns'
}

export interface LinkMetadata {
  // Edge information
  scheme?: 'one2one' | 'many2many' | 'ring';
  linkName?: string;
  linkType?: string; // Link type for filtering (nvlink, pcie, infiniband, etc.)

  // Connection details
  fromComponent?: string;
  toComponent?: string;
  fromPort?: string;
  toPort?: string;
  fromDevice?: string;
  toDevice?: string;
  deviceName?: string; // Legacy field for internal connections

  // Aggregation information
  aggregatedCount?: number; // Number of connections aggregated
  totalBandwidth?: number;  // Sum of bandwidths

  // Level of connection
  level?: 'component' | 'device' | 'rack' | 'network' | 'pod' | 'site';

  // Any additional annotations
  annotations?: Record<string, any>;
}

// ============================================================================
// Viewport and Navigation Types
// ============================================================================

export interface ViewportState {
  x: number;
  y: number;
  zoom: number;
  selectedNode?: string;
}

export interface ZoomLevel {
  level: number;
  name: string;
  nodeTypes: NodeType[];
  minZoom: number;
  maxZoom: number;
}

// ============================================================================
// Filter Types
// ============================================================================

export interface VisualizationFilters {
  showNodeTypes: NodeType[];
  showConnectionTypes: string[]; // Protocol types: 'Ethernet', 'InfiniBand', 'NVLink', etc.
  showOnlyAnnotated?: boolean;
  annotationFilters?: AnnotationFilter[];
}

export interface AnnotationFilter {
  attribute: string;
  operator: 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'greaterThan' | 'lessThan';
  value: any;
}

// ============================================================================
// Layout Types
// ============================================================================

export type AutoLayoutType = 'hierarchical' | 'force' | 'grid' | 'circular';

export interface LayoutConfig {
  autoLayoutType: AutoLayoutType;
  enableVerticalSeparation: boolean;
  layerSpacing: number;
  nodeSpacing: number;
  forceStrength: number;
}

// ============================================================================
// Statistics Types
// ============================================================================

export interface InfrastructureStats {
  totalDevices: number;
  totalInstances: number;
  totalComponents: number;
  totalConnections: number;
  deviceTypes: number;
  sites: number;
  pods: number;
  networks: number;
  racks: number;
  componentsByType: Record<string, number>;
  linksByProtocol: Record<string, number>;
}

// ============================================================================
// Search and Selection Types
// ============================================================================

export interface SearchResult {
  node: VisualizationNode;
  matchedField: string;
  matchedValue: string;
  score: number; // Relevance score
}

export interface SelectionState {
  selectedNodes: string[];
  hoveredNode?: string;
  focusedNode?: string; // Node to center viewport on
}

// ============================================================================
// Rendering Types
// ============================================================================

export interface RenderingOptions {
  showLabels: boolean;
  showComponentCounts: boolean;
  showLinkLabels: boolean;
  showLinkAggregation: boolean;
  nodeOpacity: number;
  linkOpacity: number;
  highlightConnectedNodes: boolean;
  useWebGL: boolean; // Switch between SVG and WebGL rendering
}

// ============================================================================
// Color Scheme Types
// ============================================================================

export interface ColorScheme {
  nodes: {
    site: string;
    pod: string;
    network: string;
    rack: string;
    device: string;
    component: string;
  };
  links: {
    ethernet: string;
    infiniband: string;
    nvlink: string;
    pcie: string;
    custom: string;
    unknown: string;
  };
  selection: {
    selected: string;
    hovered: string;
    connected: string;
  };
}

export const DEFAULT_COLOR_SCHEME: ColorScheme = {
  nodes: {
    site: '#1f77b4',      // Blue
    pod: '#ff7f0e',       // Orange
    network: '#17becf',   // Cyan
    rack: '#2ca02c',      // Green
    device: '#d62728',    // Red
    component: '#9467bd'  // Purple
  },
  links: {
    ethernet: '#999',         // Light gray
    infiniband: '#777',       // Dark gray
    nvlink: '#76b900',        // NVIDIA green
    pcie: '#0891b2',          // Teal
    custom: '#eab308',        // Yellow
    unknown: '#6b7280'        // Medium gray
  },
  selection: {
    selected: '#000000',      // Black
    hovered: '#ff6b6b',       // Light red
    connected: '#4ecdc4'      // Light teal
  }
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get node color based on type
 */
export function getNodeColor(node: VisualizationNode, colorScheme: ColorScheme = DEFAULT_COLOR_SCHEME): string {
  return colorScheme.nodes[node.type] || '#808080';
}

/**
 * Get link color based on protocol
 */
export function getLinkColor(protocol?: string, colorScheme: ColorScheme = DEFAULT_COLOR_SCHEME): string {
  if (!protocol) return colorScheme.links.unknown;

  const protocolLower = protocol.toLowerCase();
  if (protocolLower.includes('ethernet')) return colorScheme.links.ethernet;
  if (protocolLower.includes('infiniband')) return colorScheme.links.infiniband;
  if (protocolLower.includes('nvlink')) return colorScheme.links.nvlink;
  if (protocolLower.includes('pcie')) return colorScheme.links.pcie;
  if (protocolLower.includes('custom')) return colorScheme.links.custom;

  return colorScheme.links.unknown;
}

/**
 * Get node size based on type and zoom level
 */
export function getNodeSize(node: VisualizationNode, zoomLevel: number = 1): number {
  const baseSizes = {
    site: 20,
    pod: 15,
    network: 13,
    rack: 12,
    device: 10,
    component: 6
  };

  return baseSizes[node.type] || 8;
}

/**
 * Check if node is visible based on filters
 */
export function isNodeVisible(node: VisualizationNode, filters: VisualizationFilters): boolean {
  // Check node type filter
  if (!filters.showNodeTypes.includes(node.type)) {
    return false;
  }

  // Check annotation filters if specified
  if (filters.showOnlyAnnotated && (!node.metadata?.annotations || Object.keys(node.metadata.annotations).length === 0)) {
    return false;
  }

  // Check specific annotation filters
  if (filters.annotationFilters && filters.annotationFilters.length > 0) {
    for (const filter of filters.annotationFilters) {
      const value = node.metadata?.annotations?.[filter.attribute];
      if (!matchesFilter(value, filter)) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Check if link is visible based on filters
 */
export function isLinkVisible(link: VisualizationLink, filters: VisualizationFilters): boolean {
  // Check protocol filter
  if (link.protocol && !filters.showConnectionTypes.includes(link.protocol)) {
    return false;
  }

  return true;
}

/**
 * Helper to match filter conditions
 */
function matchesFilter(value: any, filter: AnnotationFilter): boolean {
  if (value === undefined || value === null) return false;

  switch (filter.operator) {
    case 'equals':
      return value === filter.value;
    case 'contains':
      return String(value).includes(String(filter.value));
    case 'startsWith':
      return String(value).startsWith(String(filter.value));
    case 'endsWith':
      return String(value).endsWith(String(filter.value));
    case 'greaterThan':
      return Number(value) > Number(filter.value);
    case 'lessThan':
      return Number(value) < Number(filter.value);
    default:
      return false;
  }
}

/**
 * Type guard to check if link endpoint is a string
 */
export function isLinkEndpointString(endpoint: string | VisualizationNode): endpoint is string {
  return typeof endpoint === 'string';
}

/**
 * Get the ID from a link endpoint (handles both string and object forms)
 */
export function getLinkEndpointId(endpoint: string | VisualizationNode): string {
  return typeof endpoint === 'string' ? endpoint : endpoint.id;
}
